import { Injectable } from '@angular/core';
import { HttpClient, HttpHandler, HttpHeaders, HttpParams, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, throwError, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { HTTP_STATUS_CODE } from './http-status-code.enum';
import { TokenService } from './token.service';
import { Token } from './token.model';

export type ResponseType = ArrayBuffer | Blob | string | Object;
export type HeadersType = HttpHeaders | { [header: string]: string | string[]; };
export type RequestOptions = {
    body?: any;
    headers?: HeadersType;
    observe?: 'body' | 'events' | 'response';
    params?: HttpParams | {
        [param: string]: string | string[];
    };
    responseType?: 'json' | 'arraybuffer' | 'blob' | 'text';
    reportProgress?: boolean;
    withCredentials?: boolean;
  };

@Injectable()
export class AuthHttpClient extends HttpClient {

  public onAccessDenied: Observable<HttpErrorResponse>;

  private onAccessDeniedSubject: Subject<HttpErrorResponse> = new Subject<HttpErrorResponse>();
  private REPEAT_HEADER_NAME = 'Request-Repeat';
  private REPEAT_HEADER_VALUE = 'true';

  constructor(
    httpHandler: HttpHandler,
    private tokenService: TokenService
  ) {
    super(httpHandler);

    this.onAccessDenied = this.onAccessDeniedSubject.asObservable();
  }

  request<R>(req: string | HttpRequest<any>, url?: string, options?: RequestOptions): Observable<HttpEvent<R | ResponseType> | R | ResponseType> {
    const token: string = this.tokenService.getPrefixedToken();

    if ('string' === typeof req) {
      const optionsWithAuth = this.addTokenToOptions(options, token);

      return super.request(req, url, optionsWithAuth)
        .pipe(
          catchError((error) => 
            this.handleError(error, optionsWithAuth.headers)
            .pipe(
              mergeMap(() => this.request(req, url, this.addRepeatToOptions(options)))
            )
          )
        );
    }

    const authHeaderObject = {};
    authHeaderObject[this.tokenService.getTokenHeaderName()] = token;

    const requestWithAuth = req.clone({ setHeaders: authHeaderObject });

    return super.request(requestWithAuth)
      .pipe(
        catchError((error) => 
          this.handleError(error, requestWithAuth.headers)
          .pipe(
            mergeMap(() => {
              const repeatHeaderObject = {};
              repeatHeaderObject[this.REPEAT_HEADER_NAME] = this.REPEAT_HEADER_VALUE;

              return this.request(req.clone({
                  setHeaders: repeatHeaderObject
                }));
            })
          )
        )
      );
  }

  private addRepeatToOptions(options: RequestOptions = {}): RequestOptions {
    options.headers = this.addHeader(options.headers, this.REPEAT_HEADER_NAME, this.REPEAT_HEADER_VALUE);

    return options;
  }

  private addTokenToOptions(options: RequestOptions = {}, token: string): RequestOptions {
    options.headers = this.addHeader(options.headers, this.tokenService.getTokenHeaderName(), token);

    return options;
  }

  private addHeader(headers: HeadersType = {}, name: string, value: string): HeadersType {
    if (headers instanceof HttpHeaders) {
      return headers.set(name, value);
    }

    headers[name] = value;

    return headers;
  }

  private handleError(responseError: HttpErrorResponse, requestHeaders: HeadersType): Observable<void> {
    if (
      this.hasRepeatHeader(requestHeaders) ||
      !this.tokenService.canRefreshToken() ||
      HTTP_STATUS_CODE.UNAUTHORIZED !== responseError.status
    ) {
      return this.handleAuthError(responseError);
    }

    return this.tokenService
      .refresh()
      .pipe(
        mergeMap(
          (token: Token): Observable<void> => {
            this.tokenService.setToken(token);

            if (null === token) {
              return throwError(responseError);
            }

            return of(null);
          }
        ),
        catchError(
          (): Observable<never> =>
            this.handleAuthError(responseError)
        )
      );
  }

  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    if (
      HTTP_STATUS_CODE.UNAUTHORIZED === error.status ||
      HTTP_STATUS_CODE.FORBIDDEN === error.status
    ) {
      this.onAccessDeniedSubject.next(error);
    }

    return throwError(error);
  }

  private hasRepeatHeader(headers: HeadersType): boolean {
    if (headers instanceof HttpHeaders) {
      return headers.get(this.REPEAT_HEADER_NAME) === this.REPEAT_HEADER_VALUE;
    }

    return this.REPEAT_HEADER_VALUE === headers[this.REPEAT_HEADER_NAME];
  }

}
